import { array, io, ioOption, set, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { AggregateEventInfo, EventName } from '../../../domain';
import { useUserKeyParameter } from '../../../hooks';
import { getAggregateEventLabel } from '../../utils';
import { ExtendedEventInfo } from '../types';
import { useSummaryQueryContext } from './query';
import { EventSelectionContext, EventSelectionState } from './selection';

const decorateEvent = (totalEventCount: number) => (event: AggregateEventInfo) => ({
  ...event,
  eventPercent: 100 * (event.count / totalEventCount),
  eventLabel: getAggregateEventLabel(event.eventName),
  eventTypeClass: event.eventType.toLowerCase() === 'system' ? 'System' : 'Custom',
});

const isEventNameIn = (types: string[]) => (event: AggregateEventInfo) =>
  types.includes(event.eventName);

const aggregateEventCount = (total: number, event: AggregateEventInfo) => total + event.count;

const nonEmptyString = (value: string) => !string.isEmpty(value);

export const SelectionContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const userKey = useUserKeyParameter(true);
  const { isLoading, events } = useSummaryQueryContext();
  const previousUserKey = useRef('');
  const [types, setTypes] = useState<EventName[]>([]);
  const [isNewUser, setNewUser] = useState<boolean>(false);

  const selected: ExtendedEventInfo[] = useMemo(() => {
    const totalEventCount = pipe(
      events,
      array.filter(isEventNameIn(types)),
      array.reduce(0, aggregateEventCount),
    );

    return pipe(
      events,
      array.filter(isEventNameIn(types)),
      array.map(decorateEvent(totalEventCount)),
    );
  }, [events, types]);

  // Dumb IO wrappers for mutations
  const setNewUserIO = (next: boolean) =>
    io.of(() => {
      setNewUser(next);
    });
  const setTypesIO = (next: string[]) =>
    io.of(() => {
      setTypes(next);
    });
  const setPreviousKeyIO = (next: string) =>
    io.of(() => {
      previousUserKey.current = next;
    })();

  // Reset the selection when we change users
  useEffect(() => {
    pipe(
      userKey,
      ioOption.fromNullable,
      ioOption.filter(nonEmptyString), // Only act if userKey is not empty
      ioOption.filter((k) => k !== previousUserKey.current), // Only act if userKey has changed
      ioOption.tapIO(setNewUserIO(true)), // Set isNewUser to true
      ioOption.tapIO(setTypesIO([])), // Clear types
      ioOption.tapIO(setPreviousKeyIO), // updated previousUserKey
    )();
  }, [userKey, isNewUser]);

  // Select all events when initially fetching a user
  useEffect(() => {
    pipe(
      events,
      ioOption.fromNullable<AggregateEventInfo[]>,
      ioOption.filter(() => isNewUser && !isLoading), // Only act if these conditions are true
      ioOption.map(array.map((e) => e.eventName)), // Map events to names
      ioOption.map(set.fromArray(string.Eq)), // Convert to a set to remove duplicates
      ioOption.map((s) => [...s]), // Convert back to an array
      ioOption.tapIO((t) => setTypesIO(t)()), // Set selection to all types
      ioOption.tapIO(setNewUserIO(false)), // Clear isNewUser
    )();
  }, [isNewUser, isLoading, events]);

  const context: EventSelectionState = {
    selected,
    types,
    setTypes,
  };

  return (
    <EventSelectionContext.Provider value={context}>{children}</EventSelectionContext.Provider>
  );
};
